import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbTrash, TbUserPlus } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { useApi } from "src/hooks/useApi";
import { ChangeRecipientPopup } from "src/pages/PatientPage/ChangeRecipientPopup";
import { apiRequest } from "src/services/apiClient";
import "./patientsWithoutRecipientSection.scss";

interface PatientWithoutRecipient {
    id: string;
    firstName: string;
    lastName: string;
    birthDate: string | null;
    minor: boolean;
    source: string;
    invoiceCount: number;
}

function formatBirthDate(value: string | null): string {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Patients no recipient points to — mostly minors from the practice-software sync whose file named
 *  no guardian. The roster below is built from recipients, so they'd be invisible there. Nothing can
 *  be done for them (no invoice, consent or sending) until they have a recipient, so the actions are
 *  assigning one (staff who may edit recipients) and, for a patient imported by mistake, deleting
 *  (the delete permission). A patient with invoices can't be deleted here: they'd go too, unseen. */
export function PatientsWithoutRecipientSection({ canWrite, canDelete = false, onAssigned }: { canWrite: boolean; canDelete?: boolean; onAssigned: () => void }) {
    const { t } = useTranslation();
    const { data, refetch } = useApi<PatientWithoutRecipient[]>("/patients/without-recipient");
    const [assigning, setAssigning] = useState<PatientWithoutRecipient | null>(null);
    const [deleting, setDeleting] = useState<PatientWithoutRecipient | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const patients = Array.isArray(data) ? data : [];
    if (patients.length === 0) return null;

    const closeDelete = () => {
        setDeleting(null);
        setDeleteFailed(false);
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        setDeleteFailed(false);
        try {
            await apiRequest(`/patients/${deleting.id}`, { method: "DELETE" });
            setDeleting(null);
            void refetch();
        } catch {
            setDeleteFailed(true);
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <section className="no-recipient" aria-labelledby="no-recipient-title">
            <div className="no-recipient__head">
                <TbAlertTriangle className="no-recipient__icon" aria-hidden="true" />
                <div>
                    <h2 id="no-recipient-title" className="no-recipient__title">
                        {t("recipients.noRecipient.title", { count: patients.length })}
                    </h2>
                    <p className="no-recipient__lead">{t("recipients.noRecipient.lead")}</p>
                </div>
            </div>
            <ul className="no-recipient__list">
                {patients.map((p) => {
                    const born = formatBirthDate(p.birthDate);
                    return (
                        <li key={p.id} className="no-recipient__item">
                            <span className="no-recipient__main">
                                <span className="no-recipient__name">{[p.firstName, p.lastName].filter(Boolean).join(" ")}</span>
                                <span className="no-recipient__meta">
                                    {born && t("recipients.noRecipient.born", { date: born })}
                                    {p.minor && <span className="chip chip--warning">{t("recipients.noRecipient.minor")}</span>}
                                    {p.source === "gdt_sync" && <span className="chip chip--neutral">{t("recipients.noRecipient.fromSync")}</span>}
                                </span>
                            </span>
                            {(canWrite || canDelete) && (
                                <span className="no-recipient__actions">
                                    {canDelete && (
                                        <button type="button" className="no-recipient__btn no-recipient__btn--delete" onClick={() => setDeleting(p)}>
                                            <TbTrash aria-hidden="true" /> {t("recipients.noRecipient.delete")}
                                        </button>
                                    )}
                                    {canWrite && (
                                        <button type="button" className="no-recipient__btn" onClick={() => setAssigning(p)}>
                                            <TbUserPlus aria-hidden="true" /> {t("recipients.noRecipient.assign")}
                                        </button>
                                    )}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
            {assigning && (
                <ChangeRecipientPopup
                    patientId={assigning.id}
                    allowSelf={!assigning.minor}
                    onClose={() => setAssigning(null)}
                    onChanged={() => {
                        setAssigning(null);
                        void refetch();
                        onAssigned();
                    }}
                />
            )}
            {deleting && (
                <Popup
                    title={t("patients.deleteNTitle", { count: 1 })}
                    message={
                        deleting.invoiceCount > 0 ? (
                            <p>{t("recipients.noRecipient.deleteBlocked", { count: deleting.invoiceCount })}</p>
                        ) : (
                            <>
                                <p>{t("recipients.deleteTitle")}:&nbsp;<strong>{[deleting.firstName, deleting.lastName].filter(Boolean).join(" ")}</strong>?</p>
                                {deleting.source === "gdt_sync" && <p>{t("recipients.noRecipient.deleteSyncNote")}</p>}
                                {deleteFailed && <div className="popup-warning" role="alert">{t("recipients.noRecipient.deleteFailed")}</div>}
                            </>
                        )
                    }
                    actions={
                        deleting.invoiceCount > 0
                            ? [{ label: t("popup.close"), variant: "default", onClick: closeDelete }]
                            : [
                                  { label: t("popup.cancel"), variant: "default", onClick: closeDelete },
                                  { label: t("form.delete"), variant: "danger", disabled: deleteBusy, onClick: () => void confirmDelete() },
                              ]
                    }
                    onClose={closeDelete}
                />
            )}
        </section>
    );
}
